import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider } from './contexts/AuthContext'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { LoadingSpinner } from './components/common/LoadingSpinner'
import { SalonZoekpagina } from './pages/klant/SalonZoekpagina'
const SalonSlugLayout = lazy(() => import('./components/layout/SalonSlugLayout').then((m) => ({ default: m.SalonSlugLayout })))
const KlantHubLayout = lazy(() => import('./components/layout/KlantHubLayout').then((m) => ({ default: m.KlantHubLayout })))
const AdminSalonLayout = lazy(() => import('./components/layout/AdminSalonLayout').then((m) => ({ default: m.AdminSalonLayout })))
import { RequireKlant } from './components/guard/RequireKlant'
import { RequireKlantProfielCompleet } from './components/guard/RequireKlantProfielCompleet'
import { RequireSuperadmin } from './components/guard/RequireSuperadmin'
import { RequireFullSalonAdmin } from './components/guard/RequireFullSalonAdmin'
import { RequireSuperadminSalonDashboard } from './components/guard/RequireSuperadminSalonDashboard'
import { clearChunkRecoveryReloadMarker } from './lib/chunkLoadError'
import { SentryRouteScope } from './components/monitoring/SentryRouteScope'
import { SentryDevTestButton } from './components/monitoring/SentryDevTestButton'

const Behandelingen = lazy(() => import('./pages/klant/Behandelingen').then((m) => ({ default: m.Behandelingen })))
const Betalen = lazy(() => import('./features/checkout').then((m) => ({ default: m.Betalen })))
const Bevestiging = lazy(() => import('./pages/klant/Bevestiging').then((m) => ({ default: m.Bevestiging })))
const MijnBoeking = lazy(() => import('./pages/klant/MijnBoeking').then((m) => ({ default: m.MijnBoeking })))
const MijnBoekingenPage = lazy(() => import('./pages/klant/MijnBoekingen').then((m) => ({ default: m.MijnBoekingenPage })))
const MijnProfielPage = lazy(() => import('./pages/klant/MijnProfiel').then((m) => ({ default: m.MijnProfielPage })))
const MijnBoekingRoute = lazy(() => import('./components/klant/MijnBoekingRoute').then((m) => ({ default: m.MijnBoekingRoute })))
const KlantLoginPagina = lazy(() => import('./pages/klant/KlantLoginPagina').then((m) => ({ default: m.KlantLoginPagina })))
const StelWachtwoordKlantPagina = lazy(() =>
  import('./pages/klant/StelWachtwoordKlantPagina').then((m) => ({ default: m.StelWachtwoordKlantPagina }))
)
const KlantWachtwoordResetPagina = lazy(() =>
  import('./pages/klant/KlantWachtwoordResetPagina').then((m) => ({ default: m.KlantWachtwoordResetPagina }))
)
const AdminRedirect = lazy(() => import('./pages/backoffice/AdminRedirect').then((m) => ({ default: m.AdminRedirect })))
const StelWachtwoordUitnodigingPagina = lazy(() =>
  import('./pages/auth/StelWachtwoordUitnodigingPagina').then((m) => ({ default: m.StelWachtwoordUitnodigingPagina }))
)
const TeamUitnodigingPagina = lazy(() =>
  import('./pages/auth/TeamUitnodigingPagina').then((m) => ({ default: m.TeamUitnodigingPagina }))
)
const Vragenlijst = lazy(() => import('./pages/onboarding/Vragenlijst').then((m) => ({ default: m.Vragenlijst })))
const Dashboard = lazy(() => import('./pages/backoffice/Dashboard').then((m) => ({ default: m.Dashboard })))
const AgendaV2 = lazy(() => import('./pages/backoffice/AgendaV2').then((m) => ({ default: m.AgendaV2 })))
const KlantenPage = lazy(() => import('./pages/backoffice/Klanten').then((m) => ({ default: m.KlantenPage })))
const Diensten = lazy(() => import('./pages/backoffice/Diensten').then((m) => ({ default: m.Diensten })))
const Medewerkers = lazy(() => import('./pages/backoffice/Medewerkers').then((m) => ({ default: m.Medewerkers })))
const Instellingen = lazy(() => import('./pages/backoffice/Instellingen').then((m) => ({ default: m.Instellingen })))
const BetalingPage = lazy(() => import('./pages/backoffice/Betaling').then((m) => ({ default: m.Betaling })))
const AbonnementPage = lazy(() => import('./pages/backoffice/Abonnement').then((m) => ({ default: m.Abonnement })))
const SuperadminLayout = lazy(() =>
  import('./features/superadmin/layout/SuperadminLayout').then((m) => ({ default: m.SuperadminLayout }))
)
const SalonsOverzicht = lazy(() => import('./pages/superadmin/SalonsOverzicht').then((m) => ({ default: m.SalonsOverzicht })))
const UsersOverzicht = lazy(() => import('./pages/superadmin/UsersOverzicht').then((m) => ({ default: m.UsersOverzicht })))
const SubscriptionsOverzicht = lazy(() => import('./pages/superadmin/SubscriptionsOverzicht').then((m) => ({ default: m.SubscriptionsOverzicht })))
const MollieOauthInstellingen = lazy(() => import('./pages/superadmin/MollieOauthInstellingen').then((m) => ({ default: m.MollieOauthInstellingen })))
const RevenueOverzicht = lazy(() => import('./pages/superadmin/RevenueOverzicht').then((m) => ({ default: m.RevenueOverzicht })))
const SecurityOverzicht = lazy(() => import('./pages/superadmin/SecurityOverzicht').then((m) => ({ default: m.SecurityOverzicht })))
const SuperadminKlantViewRoute = lazy(() => import('./pages/superadmin/SuperadminKlantViewRoute').then((m) => ({ default: m.SuperadminKlantViewRoute })))
const SuperadminKlantProfielBekijken = lazy(() => import('./pages/superadmin/SuperadminKlantProfielBekijken').then((m) => ({ default: m.SuperadminKlantProfielBekijken })))
const SuperadminDashboardPage = lazy(() =>
  import('./features/superadmin/dashboard/SuperadminDashboardPage').then((m) => ({ default: m.SuperadminDashboardPage }))
)
const OverGuapaaPagina = lazy(() =>
  import('./pages/public/OverGuapaaPagina').then((m) => ({ default: m.OverGuapaaPagina }))
)
const PrivacybeleidPagina = lazy(() =>
  import('./pages/public/PrivacybeleidPagina').then((m) => ({ default: m.PrivacybeleidPagina }))
)
const VoorwaardenPagina = lazy(() =>
  import('./pages/public/VoorwaardenPagina').then((m) => ({ default: m.VoorwaardenPagina }))
)

function ChunkRecoveryBootstrap () {
  useEffect(() => {
    clearChunkRecoveryReloadMarker()
  }, [])
  return null
}

export function App() {
  return (
    <BrowserRouter>
      <SentryRouteScope />
      <div className="flex min-h-dvh flex-col bg-background font-sans text-foreground antialiased">
      <Toaster
        position="bottom-right"
        richColors
        closeButton
        visibleToasts={4}
        gap={12}
        expand={true}
        style={{ zIndex: 99999 }}
      />
      <AuthProvider>
        <ChunkRecoveryBootstrap />
        <ErrorBoundary>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Start: salon zoeken (was / + landingspagina) */}
              <Route path="/" element={<Navigate to="/salon" replace />} />
              <Route path="/salon" element={<SalonZoekpagina />} />
              <Route path="/over" element={<OverGuapaaPagina />} />
              <Route path="/privacy" element={<PrivacybeleidPagina />} />
              <Route path="/voorwaarden" element={<VoorwaardenPagina />} />

              {/* Salon-specifieke boeking (salon/:slug) – index = behandelingenpagina */}
              <Route path="/salon/:slug" element={<SalonSlugLayout />}>
                <Route index element={<Behandelingen />} />
                <Route path="boeken" element={<Navigate to=".." replace />} />
                <Route path="mijn-boeking" element={<MijnBoeking />} />
                <Route path="betalen" element={<Betalen />} />
                <Route path="bevestiging" element={<Bevestiging />} />
              </Route>

              {/* Klant auth (geen hub-layout): uitnodiging / wachtwoord reset */}
              <Route path="/klant/stel-wachtwoord-in" element={<StelWachtwoordKlantPagina />} />
              <Route path="/klant/wachtwoord-reset" element={<KlantWachtwoordResetPagina />} />

              {/* Klant-portal: login, mijn-boekingen en mijn-profiel onder /klant */}
              <Route
                path="/klant"
                element={
                  <RequireKlant>
                    <RequireKlantProfielCompleet>
                      <KlantHubLayout />
                    </RequireKlantProfielCompleet>
                  </RequireKlant>
                }
              >
                <Route index element={<Navigate to="/klant/mijn-boekingen" replace />} />
                <Route path="login" element={<KlantLoginPagina />} />
                <Route path="mijn-boekingen" element={<MijnBoekingenPage />} />
                <Route path="mijn-profiel" element={<MijnProfielPage />} />
              </Route>

              {/* Legacy: oude URLs doorverwijzen naar klant-portal */}
              <Route path="/mijn-boekingen" element={<Navigate to="/klant/mijn-boekingen" replace />} />
              <Route path="/mijn-profiel" element={<Navigate to="/klant/mijn-profiel" replace />} />

              {/* Legacy klantroutes (default salon) – redirect naar salon zoeken */}
              <Route path="/behandelingen" element={<Navigate to="/salon" replace />} />
              <Route path="/boeken" element={<Navigate to="/salon" replace />} />
              <Route path="/betalen" element={<Navigate to="/salon" replace />} />
              <Route path="/bevestiging" element={<Navigate to="/salon" replace />} />
              {/* Klant-hub: mijn-boeking (losse boeking-detail) */}
              <Route
                path="/mijn-boeking"
                element={
                  <RequireKlant>
                    <RequireKlantProfielCompleet>
                      <KlantHubLayout />
                    </RequireKlantProfielCompleet>
                  </RequireKlant>
                }
              >
                <Route index element={<MijnBoekingRoute />} />
              </Route>

              {/* Backoffice – na uitnodiging eerst wachtwoord; daarna unified /login */}
              <Route path="/login/stel-wachtwoord-in" element={<StelWachtwoordUitnodigingPagina />} />
              <Route path="/team-uitnodiging/:token" element={<TeamUitnodigingPagina />} />
              <Route path="/login" element={<AdminRedirect />} />
              <Route path="/login/registreren" element={<Navigate to="/login?portal=zakelijk&mode=registreren" replace />} />
              <Route path="/onboarding/vragenlijst" element={<Vragenlijst />} />
              <Route path="/login/:salonId" element={<AdminSalonLayout />}>
                <Route
                  index
                  element={
                    <RequireSuperadminSalonDashboard>
                      <Dashboard />
                    </RequireSuperadminSalonDashboard>
                  }
                />
                <Route path="agenda" element={<Navigate to="agenda-v2" replace />} />
                <Route path="agenda-v2" element={<AgendaV2 />} />
                <Route path="klanten" element={<RequireFullSalonAdmin><KlantenPage /></RequireFullSalonAdmin>} />
                <Route path="diensten" element={<RequireFullSalonAdmin><Diensten /></RequireFullSalonAdmin>} />
                <Route path="medewerkers" element={<RequireFullSalonAdmin><Medewerkers /></RequireFullSalonAdmin>} />
                <Route path="abonnement" element={<RequireFullSalonAdmin><AbonnementPage /></RequireFullSalonAdmin>} />
                <Route path="betaling" element={<RequireFullSalonAdmin><BetalingPage /></RequireFullSalonAdmin>} />
                <Route path="instellingen" element={<RequireFullSalonAdmin><Instellingen /></RequireFullSalonAdmin>} />
              </Route>

              {/* Superadmin klant-view (buiten SuperadminLayout — volledig klantportaal) */}
              <Route
                path="/superadmin/users/:userId/klant"
                element={
                  <RequireSuperadmin>
                    <SuperadminKlantViewRoute />
                  </RequireSuperadmin>
                }
              >
                <Route index element={<Navigate to="mijn-boekingen" replace />} />
                <Route path="mijn-boekingen" element={<MijnBoekingenPage />} />
                <Route path="mijn-profiel" element={<SuperadminKlantProfielBekijken />} />
              </Route>

              {/* Superadmin */}
              <Route path="/superadmin" element={<RequireSuperadmin><SuperadminLayout /></RequireSuperadmin>}>
                <Route index element={<SuperadminDashboardPage />} />
                <Route path="salons" element={<SalonsOverzicht />} />
                <Route path="users" element={<UsersOverzicht />} />
                <Route path="subscriptions" element={<SubscriptionsOverzicht />} />
                <Route path="mollie-oauth" element={<MollieOauthInstellingen />} />
                <Route path="revenue" element={<RevenueOverzicht />} />
                <Route path="security" element={<SecurityOverzicht />} />
              </Route>

              <Route path="*" element={<Navigate to="/salon" replace />} />
            </Routes>
          </Suspense>
          <SentryDevTestButton />
        </ErrorBoundary>
      </AuthProvider>
      </div>
    </BrowserRouter>
  )
}
